import type { Router } from 'vue-router'

import { fetchAbilities } from '@/composables/useAbilities'
import { loadAuthSession } from '@/composables/useAuthSession'
import { ability } from '@/plugins/casl/ability'
import { canNavigate } from '@layouts/plugins/casl'

let abilitiesLoaded = false

export const setupGuards = (router: Router) => {
  // 👉 router.beforeEach
  // Docs: https://router.vuejs.org/guide/advanced/navigation-guards.html#global-before-guards
  router.beforeEach(async to => {
    /*
     * If it's a public route, continue navigation. This kind of pages are allowed to visited by login & non-login users. Basically, without any restrictions.
     * Examples of public routes are, 404, under maintenance, etc.
     */
    if (to.meta.public)
      return

    /**
     * Check if user is logged in by checking if the session flag cookie exists
     */
    const isLoggedIn = !!useCookie('_logged_in').value

    /*
      If user is logged in and is trying to access login like page, redirect to home
      else allow visiting the page
     */
    if (to.meta.unauthenticatedOnly) {
      if (to.query.to !== undefined)
        useCookie('accessUrl').value = String(to.query.to)

      if (isLoggedIn) {
        const accessUrl = useCookie('accessUrl').value
        const go = accessUrl || '/'

        useCookie('accessUrl').value = null

        return go
      }
      else {
        return undefined
      }
    }

    // Carrega sessão via API (lazy - só executa uma vez com sucesso)
    if (isLoggedIn) {
      const sessionOk = await loadAuthSession()

      // null = erro de rede/backend indisponível → não deslogar, deixa navegar
      // false = sessão confirmadamente inválida (401/403) → deslogar
      if (sessionOk === false) {
        const domainBase = import.meta.env.VITE_DOMAIN_BASE

        document.cookie = `_logged_in=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${domainBase}`

        return { name: 'login' }
      }
    }

    // Carrega abilities via API se o usuário está logado e ainda não foram carregadas
    if (isLoggedIn && !abilitiesLoaded) {
      try {
        const rules = await fetchAbilities()

        ability.update(rules)
        abilitiesLoaded = true
      }
      catch {
        // Se falhar, permite navegação para não bloquear o usuário
      }
    }

    if (!canNavigate(to)) {
      const queryString = new URLSearchParams(Object(to.query)).toString()
      const toPath = to.path + (queryString ? `?${queryString}` : '')

      return isLoggedIn
        ? undefined
        : {
          name: 'login',
          query: { to: to.fullPath !== '/' ? toPath : undefined },
        }
    }
  })
}

/**
 * Reseta o estado de abilities carregadas (usar no logout).
 */
export function resetAbilitiesLoaded() {
  abilitiesLoaded = false
}
